import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ExternalLink, GripVertical, Lock, Unlock,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BoardLoading from "@/components/BoardLoading";
import BoardWorkspace from "@/components/BoardWorkspace";
import CompactBoardToolbar from "@/components/CompactBoardToolbar";
import BoardDividerRow from "@/components/BoardDividerRow";
import { BoardNotesPreview } from "@/components/BoardNotesPreview";
import BoardHighlightPopover from "@/components/BoardHighlightPopover";
import {
  InformationalStatusText,
} from "@/components/InformationalStatusText";
import { locateCellView } from "@/utils/locateCell";
import { PermitReadinessLabel } from "@/components/PermitCalculatedBadge";
import MasterBoardQuickEditor from "@/components/MasterBoardQuickEditor";
import ProjectQuickStatusEditor from "@/components/ProjectQuickStatusEditor";
import SupervisorAttachmentsCell from "@/components/SupervisorAttachmentsCell";
import { useAuth } from "@/auth/AuthContext";
import { useBoardOrganization, HIGHLIGHT_CELLS } from "@/hooks/useBoardOrganization";
import { useBoardDrag } from "@/hooks/useBoardDrag";
import { useColumnResize } from "@/hooks/useColumnResize";
import { useSpreadsheetGrid } from "@/hooks/useSpreadsheetGrid";
import { useColumnVisibility } from "@/hooks/useColumnVisibility";
import BoardColumnsDialog from "@/components/BoardColumnsDialog";
import { requiresWaterLocate } from "@/utils/waterLocateRules";
import { formatBoardDate } from "@/utils/boardDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const MASTER_PROJECT_LINK_STATE = { from: "master" };

const sectionFilters = [
  ["all", "All active"],
  ["active_projects", "Active projects"],
  ["complete", "Complete"],
  ["closed", "Closed"],
  ["blocked", "Roadblocked"],
  ["canceled", "Canceled"],
];

function hasPendingPaperwork(row) {
  const permitPending = row.permit_status_detail === "pending";
  const locatePending =
    row.locate_required &&
    row.locate_status === "invalid" &&
    row.locate_status_detail === "requires_locates";
  const waterPending = row.water_locate_required && !row.water_locate_submitted_at;
  return permitPending || locatePending || waterPending;
}

const columnsForCompany = (role) => {
  const isSupervisor = role === "supervisor";
  return [
    ["project_number", "Project", true],
    ["due_date", "Due"],
    ["operational_status", "Stage"],
    ["customer_supervisor", "Cust supervisor"],
    ...(isSupervisor ? [] : [["permit_status", "Permit"]]),
    ...(isSupervisor ? [] : [["locate_status", "Locate"]]),
    ["locate_ticket_number", "Ticket"],
    ...(isSupervisor ? [] : [["water_locate", "Water"]]),
    ...(isSupervisor ? [] : [["next_scheduled_date", "Sched"]]),
    ...(isSupervisor ? [] : [["project_manager_names", "PM"]]),
    ...(isSupervisor ? [["photos", "Photos"], ["docs", "Docs"]] : []),
    ["recent_notes", "Notes"],
  ];
};

function operationalStatusLabel(row) {
  if (row.operational_status === "not_started") return "Not Started";
  if (row.operational_status === "blocked") return "Roadblocked";
  return (row.operational_status || "—").replaceAll("_", " ");
}

export default function MasterBoardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("all");
  const [paperwork, setPaperwork] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [stagePickerProject, setStagePickerProject] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({ project_code: "", client: "", city: "", water_locate_required: false });
  const [creating, setCreating] = useState(false);
  const [highlightPopover, setHighlightPopover] = useState(null); // { projectId, cellKey, current, position, sectionKey }
  const [addDividerOpen, setAddDividerOpen] = useState(false);
  const [dividerDraft, setDividerDraft] = useState({ label: "", section_key: "in_progress" });
  const { canAccessBilling, canManageProjectWorkflow, canManageRecords, hasPermission, user } = useAuth();
  const columnResize = useColumnResize({ boardKey: "master", userId: user?.id });
  const grid = useSpreadsheetGrid({
    onEnter: (rowKey) => {
      const project = (data?.rows || []).find((r) => r.id === rowKey);
      if (project) setEditingCell({ row: project });
    },
  });
  const canOrganize = hasPermission ? hasPermission("board:organize") : (user?.role === "owner_admin" || user?.role === "office_admin");
  const navigate = useNavigate();

  const loadBoard = useCallback(async () => {
    try {
      setError("");
      const response = await axios.get(`${API}/master-project-board`);
      setData(response.data);
    } catch {
      setError("The Master Project Board could not be loaded. Please try again.");
    }
  }, []);

  const boardOrg = useBoardOrganization(canOrganize, loadBoard);

  // Pointer-based row drag with idle-click fallback to the Move sheet.
  const readRowFromDom = useCallback((element) => {
    if (!element || !element.getAttribute) return null;
    const id = element.getAttribute("data-board-row-id");
    if (!id) return null;
    return {
      id,
      kind: element.getAttribute("data-board-row-kind") || "project",
      sectionKey: element.getAttribute("data-board-section-key") || "",
      version: Number(element.getAttribute("data-bo-version") || 0),
    };
  }, []);
  const drag = useBoardDrag({
    readRowFromDom,
    onDrop: async (moving, sectionKey, after) => {
      if ((boardOrg.frozenSections || []).includes(sectionKey)) {
        toast.error("This section is frozen — unfreeze it to reorder.");
        return;
      }
      const rowRef = (data?.rows || []).find((r) => r.type === moving.kind && r.id === moving.id);
      const version = rowRef?._bo_version ?? null;
      if (moving.kind === "project") {
        await boardOrg.move(moving.id, sectionKey, after, version);
      } else {
        await boardOrg.moveDivider(moving.id, sectionKey, after, version);
      }
    },
  });

  const createProject = async (event) => {
    event.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/master-project-board/projects`, {
        project_code: newProject.project_code,
        name: newProject.project_code,
        client: newProject.client,
        city: newProject.city,
        water_locate_required: newProject.water_locate_required,
      });
      toast.success("Project added to Master Board.");
      setCreateOpen(false);
      setNewProject({ project_code: "", client: "", city: "", water_locate_required: false });
      await loadBoard();
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "Project could not be added.");
    } finally { setCreating(false); }
  };

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.rows || []).filter((row) => {
      if (row.type === "divider") return true;
      if (section === "all") {
        if (["closed", "canceled"].includes(row.master_section)) return false;
      } else if (row.master_section !== section) return false;
      if (paperwork !== "all" && !hasPendingPaperwork(row)) return false;
      if (q && ![row.project_number, row.project_name, row.customer, row.next_required_action, row.remaining_work]
        .join(" ").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, paperwork, query, section]);

  const stageGroups = useMemo(() => {
    // Group by operational_status; dividers ride within their section based on
    // backend ordering (they arrive interleaved between project rows).
    const groups = new Map();
    const order = ["in_progress", "blocked", "complete", "not_started"];
    let currentSection = null;
    const ensureGroup = (state, label) => {
      if (!groups.has(state)) groups.set(state, { state, label, items: [] });
      return groups.get(state);
    };
    rows.forEach((row) => {
      if (row.type === "divider") {
        const state = row.section_key || currentSection || "in_progress";
        currentSection = state;
        const g = ensureGroup(state, state.replace("_", " "));
        g.items.push(row);
        return;
      }
      const state = row.operational_status || "not_started";
      currentSection = state;
      const g = ensureGroup(state, operationalStatusLabel(row));
      g.items.push(row);
    });
    return [...groups.values()].sort((a, b) => {
      const ai = order.indexOf(a.state); const bi = order.indexOf(b.state);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
  }, [rows]);

  const isSupervisor = user?.role === "supervisor";
  const columns = columnsForCompany(user?.role);
  const canHideColumns = canOrganize;
  const colVis = useColumnVisibility({ board: "master", columns, canHide: canHideColumns, userId: user?.id });
  const visibleColumns = colVis.visibleColumns;
  const totalCols = columns.length + 1 + (canOrganize ? 1 : 0) - colVis.hiddenCount;

  // ---- Highlight plumbing --------------------------------------------
  const openHighlight = (event, projectId, cellKey, current, sectionKey) => {
    event.preventDefault();
    if (!canOrganize || !HIGHLIGHT_CELLS.has(cellKey)) return;
    setHighlightPopover({ projectId, cellKey, current, position: { top: event.clientY, left: event.clientX }, sectionKey });
  };
  const closeHighlight = () => setHighlightPopover(null);
  const applyHighlight = async (color) => {
    if (!highlightPopover) return;
    await boardOrg.setHighlight(highlightPopover.projectId, highlightPopover.cellKey, color, highlightPopover.sectionKey);
  };

  // ---- Divider add ---------------------------------------------------
  const submitDivider = async () => {
    const label = dividerDraft.label.trim();
    if (!label) return;
    await boardOrg.createDivider(dividerDraft.section_key, label, null, null);
    setAddDividerOpen(false);
    setDividerDraft({ label: "", section_key: dividerDraft.section_key });
  };

  if (!data && !error) return <BoardLoading label="Loading Master Project Board" />;

  const summary = data?.summary || {};
  const countChips = [
    { key: "active", value: summary.active || 0, label: "Active", active: section === "active_projects", onClick: () => setSection(section === "active_projects" ? "all" : "active_projects") },
    { key: "in_progress", value: summary.in_progress || 0, label: "In Progress", active: false, onClick: () => setSection("all") },
    { key: "blocked", value: summary.blocked || 0, label: "Blocked", active: section === "blocked", onClick: () => setSection(section === "blocked" ? "all" : "blocked") },
    { key: "complete", value: summary.complete || 0, label: "Complete", active: section === "complete", onClick: () => setSection(section === "complete" ? "all" : "complete") },
  ];
  const overflowItems = [
    ...(canAccessBilling ? [
      { key: "add-project", label: "Add project", testId: "master-board-add-project", onClick: () => setCreateOpen(true) },
      { key: "add-divider", label: "Add divider", testId: "master-board-add-divider", onClick: () => setAddDividerOpen(true) },
      { key: "closed", label: "Closed projects", testId: "master-board-closed-projects-folder", to: "/closed-projects", onNavigate: () => navigate("/closed-projects") },
      { key: "deleted", label: "Deleted projects", testId: "master-board-deleted-projects-folder", to: "/deleted-projects", onNavigate: () => navigate("/deleted-projects") },
    ] : []),
    { key: "manager", label: "Open Manager Board", testId: "board-switch-to-manager", to: "/manager-board", onNavigate: () => navigate("/manager-board") },
  ];
  if (canHideColumns) {
    overflowItems.splice(overflowItems.length - 1, 0, {
      key: "columns",
      label: `Columns${colVis.hiddenCount ? ` (${colVis.hiddenCount} hidden)` : ""}`,
      testId: "master-board-columns-toggle",
      onClick: () => colVis.setOpen(true),
    });
  }

  return (
    <div className="compact-board">
      <BoardWorkspace
        eyebrow=""
        title="Master Board"
        subtitle=""
        actions={
          <CompactBoardToolbar
            countChips={countChips}
            filtersActive={paperwork !== "all"}
            onOpenFilters={() => setFiltersOpen((v) => !v)}
            onRefresh={loadBoard}
            onSearchChange={setQuery}
            overflowItems={overflowItems}
            searchValue={query}
            testIdPrefix="master-board"
          />
        }
      >
        {error ? (
          <section className="board-error-state" data-testid="master-board-error">
            <p>{error}</p>
            <button data-testid="master-board-retry-button" onClick={loadBoard} type="button">Try again</button>
          </section>
        ) : (
          <section className="board-table-panel master-board-table-panel" data-testid="master-board-table-panel">
            {filtersOpen && (
              <div className="compact-board-toolbar" data-testid="master-board-filters" style={{ borderTop: "1px solid #e2e8f0" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, color: "#64748b" }}>Section</span>
                  <select data-testid="master-section-filter" onChange={(e) => setSection(e.target.value)} value={section}>
                    {sectionFilters.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, color: "#64748b" }}>Paperwork</span>
                  <select data-testid="master-paperwork-filter" onChange={(e) => setPaperwork(e.target.value)} value={paperwork}>
                    <option value="all">All paperwork</option>
                    <option value="pending">Pending permits/locates</option>
                  </select>
                </label>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "#64748b" }} data-testid="master-board-results-count">
                  {rows.filter((r) => r.type !== "divider").length} shown
                </span>
              </div>
            )}
            <div className="board-table-scroll">
              <table className="operations-data-table spreadsheet-grid" data-testid="master-project-board-table" {...grid.tableProps}>
                <thead>
                  <tr>
                    {canOrganize && <th className="board-drag-cell" aria-label="Move" />}
                    {visibleColumns.map(([key, label, sticky]) => (
                      <th className={sticky ? "table-project-column" : ""} key={key} scope="col" style={columnResize.headerStyle(key)}>
                        <span data-mobile-label={label}>{label}</span>
                        <button
                          aria-label={`Resize ${label} column`}
                          className="col-resize-handle"
                          data-testid={`master-col-resize-${key}`}
                          onDoubleClick={() => columnResize.resetColumn(key)}
                          onPointerDown={(event) => columnResize.beginDrag(event, key)}
                          tabIndex={-1}
                          title="Drag to resize · double-click to reset"
                          type="button"
                        />
                      </th>
                    ))}
                    {!isSupervisor && (
                      <th scope="col"><span data-mobile-label="Open">Open</span></th>
                    )}
                  </tr>
                </thead>
                {stageGroups.map((group) => {
                  const frozen = (boardOrg.frozenSections || []).includes(group.state);
                  return (
                  <tbody data-testid={`master-stage-group-${group.state}`} key={group.state}>
                    <tr className="master-stage-group-row">
                      <td colSpan={totalCols} data-testid={`master-stage-divider-${group.state}`}>
                        <strong>{group.label}</strong>
                        <span>{group.items.filter((i) => i.type !== "divider").length} projects</span>
                        {frozen && (
                          <span className="board-section-frozen-badge" data-testid={`master-section-frozen-${group.state}`} title="Section frozen — reordering disabled" style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 3, color: "#b45309", fontWeight: 600 }}>
                            <Lock size={11} /> Frozen
                          </span>
                        )}
                        {canOrganize && (
                          <button
                            className="board-section-freeze-btn"
                            data-testid={`master-section-freeze-toggle-${group.state}`}
                            onClick={() => (frozen ? boardOrg.unfreezeSection(group.state) : boardOrg.freezeSection(group.state))}
                            title={frozen ? "Unfreeze section" : "Freeze section (lock ordering)"}
                            type="button"
                            style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, background: "transparent", border: "1px solid #cbd5e1", borderRadius: 4, padding: "1px 6px", cursor: "pointer" }}
                          >
                            {frozen ? <><Unlock size={12} /> Unfreeze</> : <><Lock size={12} /> Freeze</>}
                          </button>
                        )}
                      </td>
                    </tr>
                    {group.items.map((item) => item.type === "divider" ? (
                      <BoardDividerRow
                        canOrganize={canOrganize && !frozen}
                        colSpan={totalCols}
                        divider={item}
                        gripProps={canOrganize && !frozen ? drag.gripProps({ kind: "divider", id: item.id, sectionKey: group.state, version: item._bo_version }) : null}
                        key={item.id}
                        onDelete={boardOrg.deleteDivider}
                        onRename={(id, label, color, ver) => boardOrg.renameDivider(id, label, color === undefined ? item.color : color, ver)}
                        sectionKey={group.state}
                      />
                    ) : (
                      <MasterRow
                        canOrganize={canOrganize}
                        canQuickEdit={canManageProjectWorkflow || canManageRecords}
                        gridCellProps={grid.cellProps}
                        gripProps={canOrganize && !frozen ? drag.gripProps({ kind: "project", id: item.id, sectionKey: group.state, version: item._bo_version }) : null}
                        isHidden={colVis.isHidden}
                        isSupervisor={isSupervisor}
                        key={item.id}
                        onHighlightRequest={openHighlight}
                        onQuickEdit={setEditingCell}
                        onResetPosition={boardOrg.resetPosition}
                        onStagePick={() => setStagePickerProject(item)}
                        row={item}
                        rowProps={canOrganize && !frozen ? drag.rowProps({ kind: "project", id: item.id, sectionKey: group.state, version: item._bo_version }) : null}
                        sectionKey={group.state}
                      />
                    ))}
                  </tbody>
                  );
                })}
              </table>
              {rows.filter((r) => r.type !== "divider").length === 0 && (
                <div className="board-empty-state" data-testid="master-board-empty-state">
                  No projects match these filters.
                </div>
              )}
            </div>
          </section>
        )}

        {editingCell && (
          <MasterBoardQuickEditor
            canAccessBilling={canAccessBilling}
            canManageRecords={canManageRecords}
            companyContext={data?.company_context}
            initialColumn={editingCell.column}
            onClose={() => setEditingCell(null)}
            onSaved={loadBoard}
            projectId={editingCell.row.id}
          />
        )}
        {stagePickerProject && (
          <ProjectQuickStatusEditor
            canAccessBilling={canAccessBilling}
            onClose={() => setStagePickerProject(null)}
            onSaved={loadBoard}
            project={stagePickerProject}
          />
        )}
        {canAccessBilling && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogContent data-testid="master-board-create-project-dialog">
              <DialogHeader><DialogTitle>Add project</DialogTitle></DialogHeader>
              <form className="master-project-create-form" data-testid="master-board-create-project-form" onSubmit={createProject}>
                <label>Project number
                  <input data-testid="master-board-new-project-number" onChange={(e) => setNewProject({ ...newProject, project_code: e.target.value })} required value={newProject.project_code} />
                </label>
                <label>Customer
                  <input data-testid="master-board-new-project-customer" onChange={(e) => setNewProject({ ...newProject, client: e.target.value })} required value={newProject.client} />
                </label>
                <label>City
                  <input data-testid="master-board-new-project-city" onChange={(e) => {
                    const city = e.target.value;
                    setNewProject({ ...newProject, city, water_locate_required: requiresWaterLocate(city) });
                  }} required value={newProject.city} />
                </label>
                <label>Water Locate Requirement
                  <select data-testid="master-board-new-project-water-locate-requirement"
                    disabled={requiresWaterLocate(newProject.city)}
                    onChange={(e) => setNewProject({ ...newProject, water_locate_required: e.target.value === "required" })}
                    value={newProject.water_locate_required ? "required" : "not_required"}>
                    <option value="not_required">Not Required</option>
                    <option value="required">Required</option>
                  </select>
                </label>
                <DialogFooter className="master-project-create-footer">
                  <button data-testid="master-board-create-submit" disabled={creating} type="submit">
                    {creating ? "Adding…" : "Add project"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        {canOrganize && (
          <Dialog open={addDividerOpen} onOpenChange={setAddDividerOpen}>
            <DialogContent data-testid="master-board-divider-dialog">
              <DialogHeader><DialogTitle>Add divider</DialogTitle></DialogHeader>
              <form onSubmit={(e) => { e.preventDefault(); submitDivider(); }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <label>Label
                  <input autoFocus data-testid="master-board-divider-label"
                    onChange={(e) => setDividerDraft({ ...dividerDraft, label: e.target.value })}
                    placeholder="e.g. CAN'T TOUCH" required value={dividerDraft.label} />
                </label>
                <label>Section
                  <select data-testid="master-board-divider-section"
                    onChange={(e) => setDividerDraft({ ...dividerDraft, section_key: e.target.value })}
                    value={dividerDraft.section_key}>
                    <option value="in_progress">In Progress</option>
                    <option value="blocked">Roadblocked</option>
                    <option value="complete">Complete</option>
                    <option value="not_started">Not Started</option>
                  </select>
                </label>
                <DialogFooter>
                  <button data-testid="master-board-divider-submit" type="submit">Add divider</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}

        <BoardHighlightPopover
          current={highlightPopover?.current}
          onClose={closeHighlight}
          onPick={applyHighlight}
          open={!!highlightPopover}
          position={highlightPopover?.position}
        />
        {canHideColumns && (
          <BoardColumnsDialog
            columns={columns}
            dialogTestId="master-board-columns-dialog"
            hidden={colVis.hidden}
            idPrefix="master"
            onOpenChange={colVis.setOpen}
            onResetToDefault={colVis.resetToDefault}
            onShowAll={colVis.showAll}
            onToggle={colVis.toggle}
            open={colVis.open}
          />
        )}
        {drag.indicator}
        <Toaster position="bottom-right" richColors />
      </BoardWorkspace>
    </div>
  );
}

function MasterRow({
  canOrganize, canQuickEdit, gridCellProps, gripProps, isHidden, isSupervisor,
  onHighlightRequest, onMoveClick, onQuickEdit, onResetPosition, onStagePick,
  row, rowProps, sectionKey,
}) {
  const editCell = (column) => { if (canQuickEdit) onQuickEdit({ row, column }); };
  const navigate = useNavigate();
  const highlights = row.cell_highlights || {};
  const hidden = (key) => (isHidden ? isHidden(key) : false);

  const cellProps = (column) => {
    const bgColor = highlights[column];
    const style = bgColor ? {
      background: `color-mix(in srgb, var(--bo-${bgColor}, ${bgColor}) 18%, transparent)`,
      color: "#111",
    } : undefined;
    return {
      ...(gridCellProps ? gridCellProps({ rowKey: row.id, colKey: column }) : {}),
      "data-testid": `master-cell-${column}-${row.id}`,
      "data-highlight": bgColor || undefined,
      onContextMenu: (event) => onHighlightRequest(event, row.id, column, bgColor, sectionKey),
      onDoubleClick: () => editCell(column),
      style,
      title: canQuickEdit ? "Double-click to edit · Right-click to highlight" : "Right-click to highlight",
    };
  };

  return (
    <tr
      className="master-board-row"
      data-testid={`master-board-row-${row.id}`}
      data-board-row-id={row.id}
      data-board-row-kind="project"
      data-board-section-key={sectionKey}
      data-bo-version={row._bo_version ?? 0}
      {...(rowProps || {})}
    >
      {canOrganize && (
        <td className="board-drag-cell" data-testid={`master-drag-${row.id}`}>
          <button
            aria-label="Drag to reorder"
            className="board-drag-handle"
            data-testid={`master-move-${row.id}`}
            onClick={(event) => { event.stopPropagation(); }}
            type="button"
            {...(gripProps || {})}
          >
            <GripVertical size={14} />
          </button>
          {row.manual_position && <span className="board-manual-dot" data-testid={`master-manual-indicator-${row.id}`} title="Manually positioned" />}
          {row.manual_position && (
            <button
              aria-label="Reset position"
              className="board-reset-btn"
              data-testid={`master-reset-${row.id}`}
              onClick={(event) => { event.stopPropagation(); onResetPosition(row.id); }}
              type="button"
              title="Reset position"
            >↺</button>
          )}
        </td>
      )}
      <td className="table-project-column" {...cellProps("project_number")}>
        <div className="master-project-identity" data-testid={`master-project-identity-${row.id}`} style={{ minWidth: 0 }}>
          <strong style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
            {row.project_name || row.project_number}
          </strong>
          {row.project_name && row.project_name !== row.project_number && (
            <span style={{ fontSize: 11, color: "#64748b" }}>{row.project_number}</span>
          )}
        </div>
      </td>
      {!hidden("due_date") && (
      <td {...cellProps("due_date")}>{formatBoardDate(row.due_date)}</td>
      )}
      {!hidden("operational_status") && (
      <td {...cellProps("operational_status")}>
        {isSupervisor ? (
          <span
            className={`stage-pill state-${row.operational_status || "not_started"}`}
            data-testid={`master-stage-pill-${row.id}`}
          >
            {operationalStatusLabel(row)}
          </span>
        ) : (
          <span
            className={`stage-pill state-${row.operational_status || "not_started"}`}
            data-testid={`master-stage-pill-${row.id}`}
            onClick={(e) => { e.stopPropagation(); onStagePick(); }}
            role="button"
            tabIndex={0}
          >
            {operationalStatusLabel(row)}
          </span>
        )}
      </td>
      )}
      {!hidden("customer_supervisor") && (
      <td {...cellProps("customer_supervisor")}>{row.customer_supervisor || "—"}</td>
      )}
      {!isSupervisor && !hidden("permit_status") && (
        <td {...cellProps("permit_status")}>
          <PermitReadinessLabel
            reason={row.permit_status_reason}
            state={row.permit_status}
            testId={`master-permit-${row.id}`}
          />
        </td>
      )}
      {!isSupervisor && !hidden("locate_status") && (
        <td {...cellProps("locate_status")}>
          {(() => {
            const view = locateCellView(row);
            return (
              <span
                className="informational-status-text"
                data-testid={`master-locate-${row.id}`}
                title={view.label}
                style={{
                  color: view.variant.color,
                  backgroundColor: view.variant.background,
                  padding: view.variant.background === "transparent" ? 0 : "2px 6px",
                  borderRadius: 4,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {view.label}
              </span>
            );
          })()}
        </td>
      )}
      {!hidden("locate_ticket_number") && (
      <td {...cellProps("locate_ticket_number")} title={row.locate_notes || ""}>{row.locate_ticket_number || "—"}</td>
      )}
      {!isSupervisor && !hidden("water_locate") && (
        <td {...cellProps("water_locate")}><WaterLocateCell row={row} /></td>
      )}
      {!isSupervisor && !hidden("next_scheduled_date") && (
        <td {...cellProps("next_scheduled_date")}>{formatBoardDate(row.next_scheduled_date)}</td>
      )}
      {!isSupervisor && !hidden("project_manager_names") && (
        <td {...cellProps("project_manager_names")} title={row.project_manager_names?.join(", ") || ""}>
          <span style={{ display: "inline-block", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {row.project_manager_names?.join(", ") || "—"}
          </span>
        </td>
      )}
      {isSupervisor && (
        <SupervisorAttachmentsCell projectId={row.id} testIdPrefix="master-supervisor" />
      )}
      {!hidden("recent_notes") && (
      <td {...cellProps("recent_notes")} style={{ width: 300, maxWidth: 300 }}>
        <BoardNotesPreview
          onOpen={() => navigate(`/projects/${row.id}#general-project-notes`)}
          row={row}
          testId={`master-recent-notes-${row.id}`}
        />
      </td>
      )}
      {!isSupervisor && (
        <td data-testid={`master-cell-record-${row.id}`}>
          <Link
            className="table-open-link"
            data-testid={`master-open-record-${row.id}`}
            state={MASTER_PROJECT_LINK_STATE}
            to={`/projects/${row.id}`}
          >
            <ExternalLink size={13} />
          </Link>
        </td>
      )}
    </tr>
  );
}

function WaterLocateCell({ row }) {
  const water = row.water_readiness || {};
  if (!row.water_locate_required) return <span data-testid={`master-water-locate-not-required-${row.id}`}>—</span>;
  if (!row.water_locate_submitted_at) return <InformationalStatusText label="pending" testId={`master-water-locate-submission-required-${row.id}`} />;
  if (water.state === "invalid") return <InformationalStatusText label={water.reason || "invalid"} testId={`master-water-locate-invalid-${row.id}`} />;
  return <InformationalStatusText label={water.reason || "ready"} testId={`master-water-locate-ready-${row.id}`} />;
}
